import React, { useEffect, useState, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";

// #region Components
import Input from "shared/components/FormElements/Input";
import Button from "shared/components/FormElements/Button";
import CustomSelect from "shared/components/FormElements/Select";
import Card from "shared/components/UIElements/Card";
import LoadingSpinner from "shared/components/UIElements/LoadingSpinner";
import ErrorModal from "shared/components/UIElements/ErrorModal";
import Modal from "shared/components/UIElements/Modal";
// #endregion Components

// #region Utils
import { VALIDATOR_REQUIRE } from "shared/util/validators";
import { vehicletypes } from "shared/util/types";
// #endregion Utils

// #region Hooks
import { useForm } from "shared/hooks/form-hook";
import { useHttpClient } from "shared/hooks/http-hook";
import { AuthContext } from "shared/context/auth-context";
import { StoreContext } from "shared/context/store-context";
// #endregion Hooks

const VehicleItem = (props) => {
  const auth = useContext(AuthContext);
  const store = useContext(StoreContext);

  const [itemstate, setItemState] = useState();
  const [loadedItem, setLoadedItem] = useState();

  const itemId = props.itemid;
  const updateStoreItems = store.UpdateVehicles;

  const ItemTypeTR = "Araç";
  const ItemType = "vehicle";
  const ItemEndPointType = "vehicles";

  // #region Standard Interface functions
  const { isLoading, error, sendRequest, clearError } = useHttpClient();
  const navigate = useNavigate();

  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const showDeleteWarningHandler = () => {
    setShowConfirmModal(true);
  };

  const cancelDeleteHandler = () => {
    setShowConfirmModal(false);
  };
  // #endregion Standard Interface functions

  // #region Standard API calls
  const fetchItem = useCallback(async () => {
    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/${ItemEndPointType}/${itemId}`);
      setLoadedItem(responseData[ItemType]);
      setItemState(Date.now());
    } catch (err) {}
  }, [sendRequest, itemId]);

  const confirmDeleteHandler = async () => {
    setShowConfirmModal(false);
    try {
      await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/${ItemEndPointType}/${itemId}`, "DELETE", null, {
        "Content-Type": "application/json",
      });
      setItemState(Date.now());
      updateStoreItems();
      setLoadedItem(null);
      navigate(`/${ItemEndPointType}`);
    } catch (err) {}
  };
  // #endregion Standard API calls

  useEffect(() => {
    fetchItem();
  }, [itemId, fetchItem]);

  const [formState, inputHandler] = useForm(
    {
      plate: { value: "", isValid: true },
      driver: { value: "", isValid: true },
      type: { value: "", isValid: true },
      school: { value: [], isValid: true },
      company: { value: [], isValid: true },
      model: { value: [], isValid: true },
    },
    true
  );

  const itemUpdateSubmitHandler = useCallback(
    async (event) => {
      event.preventDefault();
      let responseData;
      try {
        responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/${ItemEndPointType}/${itemId}`,
          "PATCH",
          JSON.stringify({
            plate: formState.inputs.plate.value,
            driver: formState.inputs.driver.value,
            model: formState.inputs.model.value,
            type: formState.inputs.type.value,
            school: formState.inputs.school.value,
            company: formState.inputs.company.value,
          }),
          {
            "Content-Type": "application/json",
            Authorization: "Bearer " + auth.token,
          }
        );

        setLoadedItem(responseData[ItemType]);
        setItemState(Date.now());
        updateStoreItems();

        navigate(`/${ItemEndPointType}`);
      } catch (err) {}
    },
    [auth.token, sendRequest, formState, itemId, updateStoreItems, ItemEndPointType]
  );

  if (isLoading) {
    return (
      <div className="center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!loadedItem && !error) {
    return (
      <div className="center">
        <Card>
          <h2>{ItemTypeTR} bulunamadı.</h2>
        </Card>
      </div>
    );
  }
  return (
    <React.Fragment>
      <ErrorModal error={error} onClear={clearError} />
      <Modal
        show={showConfirmModal}
        onCancel={cancelDeleteHandler}
        header="Emin misiniz?"
        footerClass="place-item__modal-actions"
        footer={
          <React.Fragment>
            <Button inverse onClick={cancelDeleteHandler}>
              Hayır
            </Button>
            <Button danger onClick={confirmDeleteHandler}>
              Evet
            </Button>
          </React.Fragment>
        }
      >
        <p>{ItemTypeTR} silme işlemini onaylıyor musunuz? Bu işlem geri alınamaz.</p>
      </Modal>

      {!isLoading && loadedItem && (
        <form key={itemstate} className="item-form" onSubmit={itemUpdateSubmitHandler}>
          <Input
            id="plate"
            element="input"
            type="text"
            label="Plaka"
            validators={[VALIDATOR_REQUIRE()]}
            errorText="Plaka giriniz."
            onInput={inputHandler}
            initialValue={loadedItem.plate}
            initialValid={true}
          />
          <CustomSelect
            options={store.driverOptions}
            id="type"
            onInput={inputHandler}
            label="Sürücü"
            initialValue={store.driverOptions.find((option) => loadedItem.driver === option.value)}
            initialValid={true}
          ></CustomSelect>
          <Input
            id="model"
            element="input"
            type="text"
            label="Model"
            validators={[VALIDATOR_REQUIRE()]}
            errorText="Model giriniz."
            onInput={inputHandler}
            initialValue="Standart"
            initialValid={true}
          />
          <CustomSelect
            options={vehicletypes}
            id="type"
            onInput={inputHandler}
            label="Türü"
            initialValue={vehicletypes[0]}
            initialValid={true}
          ></CustomSelect>
          <CustomSelect
            isMulti={true}
            options={store.schoolOptions}
            id="school"
            onInput={inputHandler}
            label="Okul"
            initialValue={store.schoolOptions.filter((option) => loadedItem.school.includes(option.value))}
            initialValid={true}
          ></CustomSelect>
          <CustomSelect
            isMulti={true}
            options={store.companyOptions}
            id="company"
            onInput={inputHandler}
            label="Servis Firması"
            initialValue={store.companyOptions.filter((option) => loadedItem.company.includes(option.value))}
            initialValid={true}
          ></CustomSelect>
          <div className="button-container">
            <Button type="submit" disabled={!formState.isValid}>
              Güncelle
            </Button>

            <Button type="button" danger onClick={showDeleteWarningHandler}>
              SİL
            </Button>
          </div>
        </form>
      )}
    </React.Fragment>
  );
};

export default VehicleItem;
